import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { NoSpecSelected } from '../../components/NoSpecSelected';
import { useSpec } from '../../contexts/SpecContext';
import type { TestListItem } from '../../types/test-types';
import { ConfirmModal } from './ConfirmModal';
import { ConsoleOutput } from './ConsoleOutput';
import { useTestExecution } from './hooks/useTestExecution';
import { useTestGeneration } from './hooks/useTestGeneration';
import { useTestSelection } from './hooks/useTestSelection';
import { TestsList } from './TestsList';
import styles from './TestExecution.module.css';

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

interface TestExecutionProps {
  onOpenRightDrawer?: (content: { title: string; component: React.ReactNode }) => void;
}

export function TestExecution({
  onOpenRightDrawer: _onOpenRightDrawer,
}: TestExecutionProps = {}): React.ReactElement | null {
  const { activeSpec } = useSpec();
  const [tests, setTests] = useState<TestListItem[]>([]);
  const [loading, setLoading] = useState(false);

  const loadTests = useCallback(async () => {
    if (!activeSpec) return;

    setLoading(true);
    try {
      const API_URL = import.meta.env.VITE_API_URL;
      const response = await fetch(`${API_URL}/api/tests/${activeSpec.id}`);
      const data = await response.json();

      if (data.success) {
        setTests(data.tests || []);
      } else {
        toast.error('Failed to load tests');
      }
    } catch (error) {
      console.error('Error loading tests:', error);
      toast.error('Failed to load tests');
    } finally {
      setLoading(false);
    }
  }, [activeSpec]);

  useEffect(() => {
    if (activeSpec) {
      loadTests();
    }
  }, [activeSpec, loadTests]);

  const {
    generatingTests,
    testConsoleOutput,
    showConfirmModal,
    handleGenerateTests,
    handleConfirmGeneration,
    handleCancelGeneration,
    handleStopGeneration,
  } = useTestGeneration({ specId: activeSpec?.id, onTestsGenerated: loadTests });

  const { runningTests, runConsoleOutput, testProgress, handleRunTests, handleStopTestExecution } = useTestExecution({
    specId: activeSpec?.id,
    onTestsUpdated: loadTests,
    setTests,
  });

  const { selectedTests, toggleTest, toggleSelectAll } = useTestSelection(tests);

  if (!activeSpec) {
    return <NoSpecSelected />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>🧪 Test Execution</h1>
        <p className={styles.description}>View and execute security tests generated from hypotheses.</p>
      </div>

      <div className={styles.testsSection}>
        <div className={styles.buttonGroup}>
          <button
            className={styles.generateButton}
            onClick={handleGenerateTests}
            disabled={generatingTests || !activeSpec}
          >
            {generatingTests ? '⏳ Generating Tests...' : '🚀 Generate Tests from Hypotheses'}
          </button>

          {generatingTests && (
            <button className={styles.stopButton} onClick={handleStopGeneration} disabled={!activeSpec}>
              ⏹ Stop Generation
            </button>
          )}
        </div>

        <ConsoleOutput output={testConsoleOutput} visible={generatingTests || testConsoleOutput.length > 0} />
      </div>

      <div className={styles.testsSection}>
        <div className={styles.testsHeader}>
          <div className={styles.testsHeaderLeft}>
            <h2 className={styles.testsTitle}>📋 Generated hypotheses</h2>
            <span className={styles.testCount}>{tests.length}</span>
          </div>
          {tests.length > 0 && !runningTests && (
            <div className={styles.buttonGroup}>
              <button className={styles.runAllButton} onClick={() => handleRunTests()}>
                ▶ Run All Tests
              </button>
              {selectedTests.size > 0 && (
                <button className={styles.runSelectedButton} onClick={() => handleRunTests(Array.from(selectedTests))}>
                  ▶ Run Selected ({selectedTests.size})
                </button>
              )}
            </div>
          )}
          {runningTests && (
            <button className={styles.stopButton} onClick={handleStopTestExecution}>
              ⏹ Stop Execution
            </button>
          )}
        </div>

        {testProgress !== null && testProgress.total > 0 && runningTests && (
          <div className={styles.progressContainer}>
            <div className={styles.progressBar}>
              <div
                className={styles.progressFill}
                style={{
                  width: `${testProgress.total > 0 ? (testProgress.completed / testProgress.total) * 100 : 0}%`,
                }}
              />
            </div>
            <span className={styles.progressText}>
              {testProgress.completed}/{testProgress.total} tests completed
            </span>
          </div>
        )}

        <ConsoleOutput output={runConsoleOutput} visible={runningTests || runConsoleOutput.length > 0} />

        <TestsList
          tests={tests}
          loading={loading}
          formatDate={formatDate}
          selectedTests={selectedTests}
          onToggleTest={toggleTest}
          onToggleSelectAll={toggleSelectAll}
          disabled={runningTests}
        />
      </div>

      {showConfirmModal && (
        <ConfirmModal
          testsToDeleteCount={tests.length}
          onConfirm={handleConfirmGeneration}
          onCancel={handleCancelGeneration}
        />
      )}
    </div>
  );
}
